/**
 * What the org's lot dates are, on Settings, Plant time and shifts (B1b T3; GET and PUT /v1/twin/settings/lot-dates):
 * poured, inspected or shipped. The briefs and the consultant's summaries word a lot's date by it ("inspected on",
 * "scrapped at inspection"). An org admin chooses; every other member reads the kind in words. Shown only on the live
 * platform.
 *
 * LotDatesSettingsView is pure (drawn in tests without providers); LotDatesSettings holds the read, the draft and the
 * save. Every word is lib/brief/lot-dates'.
 */
import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { LOT_DATE_OPTIONS, type LotDateKind } from "@/lib/brief/lot-dates";
import { lotDatesErrorWords, useLotDatesQuery, useSaveLotDates } from "@/lib/api/lot-dates";
import { useBriefLive } from "@/lib/api/brief";
import { useOrg } from "@/lib/org-context";
import { Button } from "@/lib/ui/button";

export interface LotDatesSettingsViewProps {
  state: "loading" | "failed" | "ready";
  /** The kind as saved. */
  saved?: LotDateKind;
  /** The kind as chosen (the saved one until changed). */
  draft: LotDateKind;
  /** org.admin: the kind is a choice; for anyone else it is read. */
  canEdit: boolean;
  saving: boolean;
  /** The last save went through. */
  done: boolean;
  error: string | null;
  onDraft: (k: LotDateKind) => void;
  onSave: () => void;
}

const LEAD =
  "What the date on a casting lot is. The briefs and the consultant's summaries word a lot's date by it, so an inspection date never reads as a pour.";

export function LotDatesSettingsView(p: LotDatesSettingsViewProps) {
  if (p.state === "loading") {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-on-surface-muted">
        <Loader2 aria-hidden className="size-4 animate-spin" /> Reading what the lots&apos; dates are
      </p>
    );
  }
  if (p.state === "failed" || !p.saved) {
    return (
      <p role="alert" className="text-sm text-danger">
        What the lots&apos; dates are could not be read. Reload the page to try again.
      </p>
    );
  }
  const changed = p.draft !== p.saved;
  if (!p.canEdit) {
    const o = LOT_DATE_OPTIONS.find((x) => x.value === p.saved) ?? LOT_DATE_OPTIONS[0];
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p className="text-on-surface-muted">{LEAD}</p>
        <p className="text-on-surface">
          {o.label}: {o.help[0].toLowerCase() + o.help.slice(1)} An org admin changes it.
        </p>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        p.onSave();
      }}
    >
      <p className="text-on-surface-muted">{LEAD}</p>
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">What a lot&apos;s date is</legend>
        {LOT_DATE_OPTIONS.map((o) => (
          <label key={o.value} className="flex items-start gap-2">
            <input
              type="radio"
              name="lot-date-kind"
              value={o.value}
              checked={p.draft === o.value}
              onChange={() => p.onDraft(o.value)}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-on-surface">{o.label}</span>
              <span className="block text-xs text-on-surface-muted">{o.help}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!changed || p.saving}>
          {p.saving ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Save aria-hidden className="size-4" />}
          Save
        </Button>
        {changed && (
          <span className="text-xs text-on-surface-muted">The briefs are written again with it at the next analysis run.</span>
        )}
      </div>
      {p.error && (
        <p role="alert" className="text-xs text-danger">
          {p.error}
        </p>
      )}
      {p.done && !changed && !p.error && (
        <p role="status" className="text-xs text-on-surface-muted">
          Saved.
        </p>
      )}
    </form>
  );
}

/** The org's lot date kind: read, chosen and saved. Hidden off the live platform. */
export function LotDatesSettings() {
  const live = useBriefLive();
  const { can } = useOrg();
  const read = useLotDatesQuery();
  const save = useSaveLotDates();
  const [draft, setDraft] = useState<LotDateKind | null>(null);
  if (!live) return null;
  const saved = read.data?.lot_date_kind;
  return (
    <LotDatesSettingsView
      state={read.data ? "ready" : read.isError ? "failed" : "loading"}
      saved={saved}
      draft={draft ?? saved ?? "poured"}
      canEdit={can("org.admin")}
      saving={save.isPending}
      done={save.isSuccess}
      error={save.isError ? lotDatesErrorWords(save.error) : null}
      onDraft={(k) => {
        save.reset();
        setDraft(k);
      }}
      onSave={() => {
        const k = draft ?? saved;
        if (!k) return;
        save.mutate(k, { onSuccess: () => setDraft(null) });
      }}
    />
  );
}
